import auth0 from "auth0-js"
import {
  navigate
} from "gatsby"
import {
  localStore
} from "./customStorage"

const isBrowser = typeof window !== "undefined"

const authConfig = {
  domain: process.env.GATSBY_AUTH0_DOMAIN,
  clientID: process.env.GATSBY_AUTH0_CLIENTID,
  redirectUri: process.env.GATSBY_AUTH0_CALLBACK,
  responseType: "token id_token",
  scope: "openid profile email",
}

const missingAuthConfig = ["domain", "clientID", "redirectUri"].filter(
  key => !authConfig[key]
)

const logAuthDebug = (message, details = {}) => {
  console.log(`[auth] ${message}`, details)
}

const logAuthError = (message, error, details = {}) => {
  const parsedError =
    (error && (error.errorDescription || error.description || error.message || error.error)) ||
    error

  console.error(`[auth] ${message}`, {
    ...details,
    error: parsedError,
  })
}

if (!isBrowser) {
  logAuthDebug("Skipping Auth0 init during SSR")
} else if (missingAuthConfig.length > 0) {
  logAuthError("Auth0 config missing required values", null, {
    missingAuthConfig,
    configuredValues: {
      domain: Boolean(authConfig.domain),
      clientID: Boolean(authConfig.clientID),
      redirectUri: Boolean(authConfig.redirectUri),
    },
  })
} else {
  logAuthDebug("Auth0 config loaded", {
    domain: authConfig.domain,
    redirectUri: authConfig.redirectUri,
  })
}

const auth =
  isBrowser && missingAuthConfig.length === 0 ? new auth0.WebAuth(authConfig) : null

const tokens = {
  accessToken: false,
  idToken: false,
  expiresAt: false,
}

let user = {}

export const isAuthenticated = () => {
  if (!isBrowser) {
    return
  }

  return localStore.getItem("isLoggedIn") === "true"
}

export const login = () => {
  if (!isBrowser) {
    logAuthDebug("login() skipped because not in browser")
    return
  }

  if (!auth) {
    logAuthError("login() aborted because Auth0 client is not initialized", null, {
      missingAuthConfig,
      href: window.location.href,
    })
    return
  }

  try {
    logAuthDebug("Calling auth.authorize()", {
      href: window.location.href,
      redirectUri: authConfig.redirectUri,
    })
    auth.authorize()
  } catch (error) {
    logAuthError("auth.authorize() threw", error)
  }
}

const setSession = (cb = () => { }) => (err, authResult) => {
  if (err) {
    logAuthError("Auth callback returned error", err, {
      pathname: isBrowser ? window.location.pathname : undefined,
      hash: isBrowser ? window.location.hash : undefined,
    })
    navigate("/")
    cb()
    return
  }


  if (authResult && authResult.accessToken && authResult.idToken) {
    logAuthDebug("Setting authenticated session", {
      expiresIn: authResult.expiresIn,
      hasIdTokenPayload: Boolean(authResult.idTokenPayload),
    })
    let expiresAt = authResult.expiresIn * 1000 + new Date().getTime()
    tokens.accessToken = authResult.accessToken
    tokens.idToken = authResult.idToken
    tokens.expiresAt = expiresAt
    user = authResult.idTokenPayload
    localStore.setItem("isLoggedIn", true)
    if (typeof window !== "undefined" && window.location.pathname === "/callback") navigate("/admin/editor?page=b67aad8d-dbb6-457c-8786-85357b83472a")
    cb()
    return
  }

  logAuthError("Auth callback did not include expected tokens", null, {
    authResultKeys: authResult ? Object.keys(authResult) : [],
  })
  cb()
}

export const silentAuth = callback => {
  if (!isAuthenticated()) return callback()
  if (!auth) {
    logAuthError("silentAuth() aborted because Auth0 client is not initialized", null, {
      missingAuthConfig,
    })
    callback()
    return
  }

  try {
    logAuthDebug("Running auth.checkSession()")
    auth.checkSession({}, setSession(callback))
  } catch (error) {
    logAuthError("auth.checkSession() threw", error)
    callback()
  }
}

export const handleAuthentication = () => {
  if (!isBrowser) {
    return
  }

  if (!auth) {
    logAuthError("handleAuthentication() aborted because Auth0 client is not initialized", null, {
      missingAuthConfig,
      href: window.location.href,
    })
    return
  }

  try {
    logAuthDebug("Parsing Auth0 hash", {
      pathname: window.location.pathname,
      hasHash: Boolean(window.location.hash),
    })
    auth.parseHash(setSession())
  } catch (error) {
    logAuthError("auth.parseHash() threw", error)
  }
}

export const getProfile = () => {
  return user
}

export const logout = () => {
  localStore.setItem("isLoggedIn", false)
  if (!auth) {
    logAuthError("logout() skipped because Auth0 client is not initialized", null, {
      missingAuthConfig,
    })
    return
  }

  auth.logout()
}